import { type FormEvent, useEffect, useState } from 'react';
import { loadStripe, type StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, LinkAuthenticationElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { stripePublicKey } from '../../../environments/environment';
import { Button } from '@startuptools/ui/Button';
import { Dialog, DialogContent } from '@startuptools/ui/Dialog';
import { Stack } from '@startuptools/ui/Stack';
import { DialogTitle } from '@startuptools/ui/Dialog';
import { useStripePaymentIntentLazyQuery } from '../../graphql/react-operations';
import { isNil } from 'lodash-es';
import { StripeCurrencyFormat } from '@startuptools/ui/StripeCurrencyFormat';
import { Trans, useLingui } from '@lingui/react';

const stripePromise = loadStripe(stripePublicKey);

interface Props {
  companyId: string;
  paymentIntentId: string | null | undefined;
  onCancel: () => void;
  onPaid: () => void;
}

export function PaymentDialog({ companyId, paymentIntentId, onCancel, onPaid }: Props) {
  const [query, { loading, data, error }] = useStripePaymentIntentLazyQuery({
    variables: { companyId, paymentIntentId: paymentIntentId! },
  });

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!isNil(paymentIntentId) && isNil(data)) {
      void query();
    }
  });

  if (isNil(paymentIntentId)) {
    onCancel();
    return;
  }

  if (isNil(data) || loading) {
    return <h4>Loading...</h4>;
  }
  if (error) {
    console.error(error);
    return;
  }
  const paymentIntent = data.stripePaymentIntent;

  const options = {
    clientSecret: paymentIntent.client_secret,
    loader: 'always',
    // @ts-expect-error-auto
    payment_method_creation: 'manual',
  } satisfies StripeElementsOptions;

  return (
    <Dialog open>
      <DialogTitle onClose={onCancel}>Betala</DialogTitle>
      <DialogContent>
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm amount={paymentIntent.amount} currency={paymentIntent.currency} onPaid={() => onPaid()} />
        </Elements>
      </DialogContent>
    </Dialog>
  );
}

const CheckoutForm = ({ amount, currency, onPaid }: { amount: number; currency: string; onPaid: () => void }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { i18n } = useLingui();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    setLoading(true);
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const result = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      redirect: 'if_required',
      confirmParams: {
        save_payment_method: true,
        return_url: window.location.href,
      },
    });

    if (result.error) {
      setErrorMsg(result.error.message ?? null);
      setLoading(false);
    } else {
      // Beware, we might not reach this point if any other payment method than card
      // Not something we need to worry about until/unless we accept other kinds later on
      setLoading(false);
      onPaid();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack direction="column" gap={4}>
        <LinkAuthenticationElement />
        <PaymentElement />
        {errorMsg && <p className="max-w-[300px]">{errorMsg}</p>}
        <Button type="submit" disabled={!stripe} loading={loading}>
          <Trans
            id="Pay <currencyFormat>{amount}</currencyFormat>"
            values={{ amount }}
            components={{ currencyFormat: <StripeCurrencyFormat locale={i18n.locale} currency={currency} /> }}
          />
        </Button>
      </Stack>
    </form>
  );
};
